import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { XIcon, DocumentDuplicateIcon } from "@heroicons/react/outline";
import {
  getBranchesThunk,
  copyExchangeChargesThunk,
} from "../../../store/thunks/branchThunks";

const ExchangeChargesCopyModal = ({ isOpen, onClose, sourceBranch }) => {
  const dispatch = useDispatch();
  const { branches, loading } = useSelector((state) => state.branch);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Load all branches when modal opens
      dispatch(getBranchesThunk({ limit: 100, isActive: true }));
    }
  }, [isOpen, dispatch]);

  const handleSelectAll = () => {
    const eligibleBranches = branches
      .filter((b) => b._id !== sourceBranch._id && b.isActive)
      .map((b) => b._id);
    setSelectedBranches(eligibleBranches);
  };

  const handleDeselectAll = () => {
    setSelectedBranches([]);
  };

  const handleToggleBranch = (branchId) => {
    if (selectedBranches.includes(branchId)) {
      setSelectedBranches(selectedBranches.filter((id) => id !== branchId));
    } else {
      setSelectedBranches([...selectedBranches, branchId]);
    }
  };

  const handleCopy = async () => {
    if (selectedBranches.length === 0) {
      alert("Please select at least one branch to copy to");
      return;
    }

    if (
      !window.confirm(
        `Copy exchange charges from ${sourceBranch.name} to ${selectedBranches.length} branch(es)? This will override their existing GOLD Exchange Charges Configuration.`
      )
    ) {
      return;
    }

    setCopying(true);
    try {
      const result = await dispatch(
        copyExchangeChargesThunk({
          sourceBranchId: sourceBranch._id,
          targetBranchIds: selectedBranches,
        })
      ).unwrap();

      alert(
        `Successfully copied to ${result.data.updated.length} branches.${
          result.data.failed.length > 0
            ? ` Failed for ${result.data.failed.length} branches.`
            : ""
        }`
      );
      onClose();
    } catch (error) {
      alert(`Failed to copy exchange charges: ${error}`);
    } finally {
      setCopying(false);
    }
  };

  const filteredBranches = branches.filter(
    (branch) =>
      branch._id !== sourceBranch._id &&
      branch.isActive &&
      (branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branch.address.city.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <DocumentDuplicateIcon className="h-6 w-6" />
              Copy GOLD Exchange Charges Configuration
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Copy from:{" "}
              <span className="font-medium">
                {sourceBranch.name} ({sourceBranch.code})
              </span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg"
            disabled={copying}
          >
            <XIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Search and Actions */}
        <div className="p-4 border-b">
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Search branches by name, code, or city..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1 px-4 py-2 border rounded-lg"
            />
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
            >
              Select All
            </button>
            <button
              onClick={handleDeselectAll}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
            >
              Clear All
            </button>
          </div>
          <div className="mt-2 text-sm text-gray-600">
            {selectedBranches.length} branch(es) selected
          </div>
        </div>

        {/* Branch List */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredBranches.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No branches found
            </div>
          ) : (
            <div className="space-y-2">
              {filteredBranches.map((branch) => (
                <label
                  key={branch._id}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                    selectedBranches.includes(branch._id)
                      ? "bg-blue-50 border-blue-300"
                      : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedBranches.includes(branch._id)}
                    onChange={() => handleToggleBranch(branch._id)}
                    className="h-4 w-4"
                  />
                  <div className="flex-1">
                    <div className="font-medium">{branch.name}</div>
                    <div className="text-sm text-gray-600">
                      {branch.code} • {branch.address.city},{" "}
                      {branch.address.state}
                    </div>
                    {branch.settings?.exchangeCharges && (
                      <div className="text-xs text-orange-600 mt-1">
                        Has custom exchange charges (will be overwritten)
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <span
                      className={`inline-block px-2 py-1 text-xs rounded-full ${
                        branch.branchType === "FLAGSHIP"
                          ? "bg-purple-100 text-purple-800"
                          : branch.branchType === "STANDARD"
                          ? "bg-blue-100 text-blue-800"
                          : branch.branchType === "FRANCHISE"
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {branch.branchType}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t flex justify-between items-center">
          <div className="text-sm text-gray-600">
            This will override the Exchange Charges Configuration (Only For
            GOLD) for all selected branches.
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
              disabled={copying}
            >
              Cancel
            </button>
            <button
              onClick={handleCopy}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                selectedBranches.length === 0
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-600 hover:bg-blue-700 text-white"
              }`}
              disabled={selectedBranches.length === 0 || copying}
            >
              {copying ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Copying...
                </>
              ) : (
                <>
                  <DocumentDuplicateIcon className="h-4 w-4" />
                  Copy to {selectedBranches.length} Branch(es)
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExchangeChargesCopyModal;
